import {
  DestroyRef,
  Injectable,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { selectImpersonatedUser } from '@core/auth/auth.selectors';
import { toAuthorityProblem } from '@core/auth/application-authority-problem';
import { SupportImpersonationHttpService } from '@core/services/api-client.generated';
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData,
} from '@shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { SupportImpersonationDrafts } from './support-impersonation-drafts';
import {
  SUPPORT_IMPERSONATION_REFUSALS,
  SupportImpersonationEditHandle,
} from './support-impersonation-edit-handle';

export type SupportImpersonationState =
  /** Not impersonating anyone. */
  | { status: 'inactive' }
  | { status: 'checking' }
  /** 404: the M2b contract is off here, so impersonation behaves as it always has. */
  | { status: 'not-enforced' }
  | { status: 'read-only' }
  | { status: 'entering' }
  | { status: 'edit'; expiresAt: Date }
  | { status: 'leaving' }
  /** The API refused this impersonation (for example the actor no longer holds ImpersonateUsers). */
  | { status: 'refused'; code: string | null }
  /** Authority could not be established just now; nothing is assumed. */
  | { status: 'unavailable' };

/** Why the tab left edit mode: `closed` is deliberate (S6); `reset` is a reload, target change or end (S5). */
export type EditExitReason = 'closed' | 'reset';

/**
 * M2b support impersonation in this tab (#3335 S4–S6). Read-only until the operator deliberately
 * enters edit mode; the edit-context handle lives only in memory ({@link SupportImpersonationEditHandle}),
 * so a reload, a target change or the end of impersonation always returns to read-only, and the server
 * contexts are ended as well. Presentation only: the API decides every request.
 */
@Injectable({ providedIn: 'root' })
export class SupportImpersonationService {
  private readonly _api = inject(SupportImpersonationHttpService);
  private readonly _handle = inject(SupportImpersonationEditHandle);
  private readonly _drafts = inject(SupportImpersonationDrafts);
  private readonly _dialog = inject(MatDialog);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _store = inject(Store);

  private readonly _state = signal<SupportImpersonationState>({ status: 'inactive' });
  private _expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private _targetId: string | null = null;
  private _booted = false;
  private _generation = 0;

  readonly state = this._state.asReadonly();

  /** Whether the M2b contract applies here and the tab may offer the edit-mode switch. */
  readonly canToggleEdit = computed(() => {
    const status = this._state().status;
    return status === 'read-only' || status === 'edit';
  });

  readonly isEditing = computed(() => this._state().status === 'edit');

  constructor() {
    const impersonated = this._store.selectSignal(selectImpersonatedUser);
    effect(() => {
      const targetId = impersonated()?.id ?? null;
      untracked(() => void this._onTarget(targetId));
    });

    this._handle.refusals
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(({ code, sentWith }) => this._onRefusal(code, sentWith));
  }

  /** Re-read the mode from the server for the current target and this tab's handle. */
  async refresh(): Promise<void> {
    if (this._targetId === null) {
      this._state.set({ status: 'inactive' });
      return;
    }

    const generation = this._generation;
    this._state.set({ status: 'checking' });
    try {
      const session = await firstValueFrom(this._api.getSession());
      if (generation !== this._generation) return;
      if (session.mode === 'edit' && this._handle.handle() && session.editExpiresUtc) {
        this._enterEdit(new Date(session.editExpiresUtc));
      } else {
        this._handle.clear();
        this._state.set({ status: 'read-only' });
      }
    } catch (error) {
      if (generation !== this._generation) return;
      this._handle.clear();
      this._state.set(this._fromProblem(error));
    }
  }

  /** Deliberately enter edit mode (S4): ask the server for a new, short-lived edit context. */
  async enterEditMode(): Promise<void> {
    if (this._state().status !== 'read-only') return;
    const generation = this._generation;
    this._state.set({ status: 'entering' });
    try {
      const context = await firstValueFrom(this._api.openEditContext());
      if (generation !== this._generation) {
        // The target changed or impersonation ended meanwhile: never keep a handle for it.
        void this._endOnServer('reset');
        return;
      }
      this._handle.set(context.handle);
      this._enterEdit(new Date(context.expiresUtc));
    } catch (error) {
      if (generation !== this._generation) return;
      const state = this._fromProblem(error);
      this._state.set(state.status === 'unavailable' ? { status: 'read-only' } : state);
      this._snackbar.open(
        state.status === 'unavailable'
          ? 'Edit mode could not be started just now. Try again shortly.'
          : 'Edit mode is not available for this impersonation.',
        'Dismiss',
        { duration: 6000 }
      );
    }
  }

  /**
   * Deliberately leave edit mode (S6). With unsaved changes, offer Save, Discard or Cancel: Save must be
   * confirmed by the server before switching to read-only (a failed save keeps edit mode); Discard drops
   * the changes; Cancel stays in edit mode. Resolves to whether the tab is now read-only.
   */
  async leaveEditMode(): Promise<boolean> {
    if (this._state().status !== 'edit') return this._state().status !== 'leaving';
    const generation = this._generation;
    const unsaved = this._drafts.unsaved();
    if (unsaved.length > 0) {
      const choice = await firstValueFrom(
        this._dialog
          .open<ConfirmationDialogComponent, IConfirmationDialogData<boolean>, boolean | null>(
            ConfirmationDialogComponent,
            {
              data: {
                title: 'Save your changes before leaving edit mode?',
                contentHtml:
                  'You are editing as the impersonated user. Save the changes now, discard them, or cancel to keep editing.',
                okText: 'SAVE',
                thirdButton: { text: 'DISCARD', value: false },
              },
              width: '520px',
            }
          )
          .afterClosed()
      );
      if (choice === null || choice === undefined) {
        return false; // CANCEL: stay in edit mode
      }

      if (generation !== this._generation || this._state().status !== 'edit') {
        // The target changed, impersonation ended or edit mode was lost while the prompt was open.
        return false;
      }

      if (choice === true) {
        const saved = await this._saveAll(unsaved);
        if (!saved) {
          this._snackbar.open(
            'Your changes could not be saved. Edit mode stays on so you can retry or discard them.',
            'Dismiss',
            { duration: 8000 }
          );
          return false;
        }
      } else {
        unsaved.forEach((draft) => draft.discard());
      }
    }

    if (generation !== this._generation || this._state().status !== 'edit') {
      return false;
    }

    await this._exit('closed');
    return true;
  }

  private async _saveAll(drafts: { save(): Promise<boolean> }[]): Promise<boolean> {
    for (const draft of drafts) {
      try {
        if (!(await draft.save())) return false;
      } catch {
        return false;
      }
    }

    return true;
  }

  private async _onTarget(targetId: string | null): Promise<void> {
    const changed = targetId !== this._targetId;
    const firstRun = !this._booted;
    this._booted = true;
    if (!changed && !firstRun) return;

    const previous = this._targetId;
    this._targetId = targetId;
    this._generation++;
    // S5: a target change, the end of impersonation, or a reload (the first run while impersonating)
    // always returns to read-only and ends the server contexts this operator may still hold.
    const hadContext = this._handle.handle() !== null;
    this._clearLocal();
    const generation = this._generation;
    if (hadContext || previous !== null || (firstRun && targetId !== null)) {
      await this._endOnServer('reset');
    }

    if (generation !== this._generation) {
      return; // a newer target change has taken over
    }

    if (targetId === null) {
      this._state.set({ status: 'inactive' });
      return;
    }

    await this.refresh();
  }

  private _onRefusal(code: string, sentWith: string | null): void {
    if (!SUPPORT_IMPERSONATION_REFUSALS.has(code) || this._targetId === null) return;
    // A read-only or stale refusal of a request sent before the current context was opened says
    // nothing about the current context. A refusal of the impersonation itself always applies.
    const aboutTheContext =
      code === 'impersonation-read-only' || code === 'impersonation-edit-context-invalid';
    if (aboutTheContext && sentWith !== this._handle.handle()) return;
    const wasEditing = this._state().status === 'edit';
    this._generation++;
    this._clearLocal();
    if (code === 'impersonation-read-only' || code === 'impersonation-edit-context-invalid') {
      this._state.set({ status: 'read-only' });
      this._snackbar.open(
        wasEditing
          ? 'Edit mode has ended, so that change was not made. You are back in read-only mode.'
          : 'Impersonation is read-only, so that change was not made. Turn on edit mode first.',
        'Dismiss',
        { duration: 8000 }
      );
      return;
    }

    this._state.set({ status: 'refused', code });
    this._snackbar.open('This impersonation is no longer allowed. Stop impersonating to continue.', 'Dismiss', {
      duration: 10000,
    });
  }

  private _enterEdit(expiresAt: Date): void {
    this._cancelTimer();
    const remaining = expiresAt.getTime() - Date.now();
    if (remaining <= 0) {
      this._clearLocal();
      this._state.set({ status: 'read-only' });
      return;
    }

    this._state.set({ status: 'edit', expiresAt });
    this._expiryTimer = setTimeout(() => {
      this._clearLocal();
      this._state.set({ status: 'read-only' });
      this._snackbar.open('Edit mode has ended (time limit). You are back in read-only mode.', 'Dismiss', {
        duration: 8000,
      });
    }, remaining);
  }

  private async _exit(reason: EditExitReason): Promise<void> {
    const generation = this._generation;
    this._state.set({ status: 'leaving' });
    // Drop the handle first, so nothing sent from now on can change anything.
    this._clearLocal();
    await this._endOnServer(reason);
    // A target change, Stop or refusal during the request has already set the right state.
    if (generation === this._generation && this._state().status === 'leaving') {
      this._state.set({ status: 'read-only' });
    }
  }

  private async _endOnServer(reason: EditExitReason): Promise<void> {
    try {
      await firstValueFrom(this._api.endEditContexts(reason));
    } catch {
      // Best effort: the handle is gone from this tab and the server context expires on its own.
    }
  }

  private _clearLocal(): void {
    this._cancelTimer();
    this._handle.clear();
  }

  private _cancelTimer(): void {
    if (this._expiryTimer !== null) {
      clearTimeout(this._expiryTimer);
      this._expiryTimer = null;
    }
  }

  private _fromProblem(error: unknown): SupportImpersonationState {
    const problem = toAuthorityProblem(error);
    switch (problem.status) {
      case 404:
        return { status: 'not-enforced' };
      case 503:
        return { status: 'unavailable' };
      case 403:
      case 409:
        return { status: 'refused', code: problem.code };
      default:
        return { status: 'unavailable' };
    }
  }
}
